// usePermissions Hook - Check user permissions

import { useCallback, useMemo } from 'react';
import { useAppSelector } from '../../../app/hooks';
import type { Permission } from '../types';
import { ROLE_PERMISSIONS } from '../constants';

/**
 * Hook for checking user permissions
 *
 * @example
 * ```tsx
 * const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();
 *
 * if (hasPermission('borefield:create')) {
 *   // Show create button
 * }
 *
 * if (hasAnyPermission(['bore:update', 'bore:delete'])) {
 *   // Show edit options
 * }
 * ```
 */
export function usePermissions() {
  const user = useAppSelector((state) => state.auth?.user);
  const isAuthenticated = useAppSelector((state) => state.auth?.isAuthenticated ?? false);

  /**
   * Get all permissions for the current user's role
   */
  const userPermissions = useMemo(() => {
    if (!user?.role) return [];
    return ROLE_PERMISSIONS[user.role] ?? [];
  }, [user?.role]);

  /**
   * Check if user has a specific permission
   */
  const hasPermission = useCallback(
    (permission: Permission): boolean => {
      if (!isAuthenticated || !user) return false;

      // Admin has all permissions
      if (userPermissions.includes('*')) return true;

      return userPermissions.includes(permission);
    },
    [isAuthenticated, user, userPermissions]
  );

  /**
   * Check if user has any of the specified permissions
   */
  const hasAnyPermission = useCallback(
    (permissions: Permission[]): boolean => {
      return permissions.some(hasPermission);
    },
    [hasPermission]
  );

  /**
   * Check if user has all of the specified permissions
   */
  const hasAllPermissions = useCallback(
    (permissions: Permission[]): boolean => {
      return permissions.every(hasPermission);
    },
    [hasPermission]
  );

  /**
   * Check if user can perform an action on a specific resource
   * Convenience method for common resource:action patterns
   */
  const canPerform = useCallback(
    (action: 'read' | 'create' | 'update' | 'delete', resource: string): boolean => {
      const permission = `${resource}:${action}` as Permission;
      return hasPermission(permission);
    },
    [hasPermission]
  );

  /**
   * Check if user is an admin
   */
  const isAdmin = useMemo(() => {
    return user?.role === 'admin' || user?.role === 'super_admin';
  }, [user?.role]);

  /**
   * Check if user can manage other users
   */
  const canManageUsers = useMemo(() => {
    return hasPermission('admin:users');
  }, [hasPermission]);

  return {
    user,
    isAuthenticated,
    userPermissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canPerform,
    isAdmin,
    canManageUsers,
  };
}

export default usePermissions;
